if (!customElements.get("wd-video-grid")) {
  customElements.define(
    "wd-video-grid",
    class WDVideoGrid extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.soundUnlocked = false;
        this.isVisible = false;
        this.swiper = null;
        this._visibilityObserver = null;
        this._initInterval = null;
        this._onSlideChange = null;
        this._resizeHandler = this.onResize.bind(this);
        this._suppressExclusivePlay = false;
        this._onVideoPlay = this.handleVideoPlay.bind(this);
      }

      connectedCallback() {
        this.swiperEl = this.querySelector("[data-swiper]");
        this.refreshVideos();
        this.prepareVideos();
        this.wireSoundToggles();
        this.addEventListener("play", this._onVideoPlay, true);
        this._initInterval = setInterval(() => this.tryInit(), 300);
        window.addEventListener("resize", this._resizeHandler);
      }

      disconnectedCallback() {
        clearInterval(this._initInterval);
        this._visibilityObserver?.disconnect();
        window.removeEventListener("resize", this._resizeHandler);
        this.removeEventListener("play", this._onVideoPlay, true);
        if (this.swiper && this._onSlideChange) {
          this.swiper.off("slideChange", this._onSlideChange);
        }
      }

      handleVideoPlay(event) {
        const video = event.target;
        if (video.tagName !== "VIDEO" || !this.contains(video)) return;
        this.enforceSinglePlayback(video);
      }

      enforceSinglePlayback(activeVideo) {
        if (this._suppressExclusivePlay) return;

        const item = activeVideo.closest(".wd-video-grid__li");
        if (item) {
          const items = [...this.querySelectorAll(".wd-video-grid__li")];
          const index = items.indexOf(item);
          if (index >= 0) this.currentIndex = index;
        }

        this._suppressExclusivePlay = true;
        this.querySelectorAll(".wd-video-grid__li video").forEach((video) => {
          if (video !== activeVideo) {
            video.pause();
          }
        });
        this._suppressExclusivePlay = false;
      }

      prepareVideos() {
        this.videos.forEach((video) => {
          video.loop = false;
          video.autoplay = false;
          video.removeAttribute("autoplay");
        });
      }

      refreshVideos() {
        const items = [...this.querySelectorAll(".wd-video-grid__li")];
        this.videos = items
          .map((item) => this.getVisibleVideo(item))
          .filter(Boolean);
      }

      getVisibleVideo(item) {
        const videos = [...item.querySelectorAll("video")];
        if (videos.length === 0) return null;
        if (videos.length === 1) return videos[0];

        const isMobile = window.matchMedia("(max-width: 899px)").matches;
        return (
          videos.find((video) => {
            if (isMobile) return !video.classList.contains("wt-mobile-hidden");
            return !video.classList.contains("wt-desktop-hidden");
          }) || videos[0]
        );
      }

      tryInit() {
        const hasMobileCarousel = this.swiperEl?.classList.contains(
          "wd-video-grid__swiper--mobile",
        );
        const isMobileViewport = window.matchMedia("(max-width: 899px)").matches;

        // Swiper only initializes below 900px — don't block desktop init waiting for it
        if (
          hasMobileCarousel &&
          isMobileViewport &&
          !this.swiperEl.classList.contains("swiper-initialized")
        ) {
          return;
        }

        clearInterval(this._initInterval);
        this._initInterval = null;

        this.refreshVideos();
        this.prepareVideos();

        this.swiper =
          isMobileViewport && this.swiperEl?.swiper
            ? this.swiperEl.swiper
            : null;

        if (this.swiper) {
          this._onSlideChange = () => this.handleSlideChange(this.swiper);
          this.swiper.on("slideChange", this._onSlideChange);
          this.currentIndex = this.swiper.activeIndex;
        } else {
          this.currentIndex = 0;
        }

        this.videos.forEach((video, index) => {
          video.addEventListener("ended", () => this.handleVideoEnded(index));
        });

        if (!this._visibilityObserver) {
          this.observeVisibility();
        } else if (this.isVisible) {
          this.playCurrent();
        }
      }

      observeVisibility() {
        this._visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isVisible = entry.isIntersecting;
              if (this.isVisible) {
                this.playCurrent();
              } else {
                this.pauseAll();
              }
            });
          },
          { threshold: 0.2 },
        );
        this._visibilityObserver.observe(this);
      }

      wireSoundToggles() {
        this.querySelectorAll(".wt-hero-video__sound-toggle").forEach((btn) => {
          btn.addEventListener("click", () => {
            requestAnimationFrame(() => {
              const isUnmuted =
                btn.classList.contains("wt-hero-video__sound-toggle--unmuted") ||
                btn.dataset.sound === "on";

              this.soundUnlocked = isUnmuted;

              const active = this.videos[this.currentIndex];
              if (active && isUnmuted) {
                active.muted = false;
                active.play().catch(() => {});
              }
            });
          });
        });
      }

      onResize() {
        this.refreshVideos();
        if (this.isVisible) this.playCurrent();
      }

      pauseAll() {
        this.videos.forEach((video) => {
          video.pause();
          video.muted = true;
        });
      }

      handleSlideChange(swiper) {
        this.currentIndex = swiper.activeIndex;
        this.playCurrent({ fromSlideChange: true });
      }

      handleVideoEnded(index) {
        if (index !== this.currentIndex || !this.isVisible) return;

        const nextIndex = (index + 1) % this.videos.length;
        const isMobileCarousel =
          this.swiper && window.matchMedia("(max-width: 899px)").matches;

        if (isMobileCarousel && this.swiper.activeIndex !== nextIndex) {
          this.swiper.slideTo(nextIndex);
          return;
        }

        this.currentIndex = nextIndex;
        this.playCurrent();
      }

      playCurrent({ fromSlideChange = false } = {}) {
        if (!this.isVisible || this.videos.length === 0) return;

        const video = this.videos[this.currentIndex];
        if (!video) return;

        this.videos.forEach((v, i) => {
          if (i !== this.currentIndex) {
            v.pause();
            v.currentTime = 0;
            v.muted = true;
          }
        });

        // Autoplay always muted until user unmutes (desktop + mobile)
        const allowSound = this.soundUnlocked && !fromSlideChange;
        this.playVideo(video, allowSound).then((playedWithSound) => {
          if (playedWithSound) this.soundUnlocked = true;
        });
      }

      async playVideo(video, allowSound) {
        this._suppressExclusivePlay = true;

        try {
          if (allowSound) {
            video.muted = false;
            try {
              await video.play();
              return true;
            } catch {
              /* browser blocked unmuted autoplay */
            }
          }

          video.muted = true;
          try {
            await video.play();
          } catch (err) {
            console.warn("wd-video-grid: autoplay prevented", err);
          }
          return false;
        } finally {
          this._suppressExclusivePlay = false;
        }
      }
    },
  );
}
